import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, BookOpen, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";

export default function CarnetsLibrary() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/public/carnets").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#1A1B18]">
      <Helmet>
        <title>Les Carnets de Voyage DealTravel | Voyages réels sur-mesure</title>
        <meta name="description" content="Découvrez de véritables carnets de voyage conçus pour nos voyageurs — itinéraires, hôtels et expériences composés sur-mesure par DealTravel." />
        <link rel="canonical" href="https://dealtravel.eu/carnets" />
      </Helmet>
      <Nav />

      <header className="pt-36 pb-16 px-6 md:px-12 border-b border-[#EAE8E3]">
        <div className="max-w-[1400px] mx-auto">
          <p className="label-eyebrow">Les Carnets de Voyage DealTravel</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.03] max-w-3xl">
            De véritables voyages, racontés page après page.
          </h1>
          <p className="mt-6 text-[#737373] leading-relaxed max-w-2xl">
            Découvrez de véritables carnets de voyage conçus pour nos voyageurs. Chaque carnet est un voyage
            réellement composé — une source d'inspiration pour imaginer le vôtre.
          </p>
        </div>
      </header>

      <main className="py-16 md:py-20 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          {items === null ? (
            <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#96836B]" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-20" data-testid="carnets-empty">
              <BookOpen className="h-10 w-10 text-[#96836B] mx-auto" strokeWidth={1.2} />
              <p className="font-serif-display text-2xl mt-5">Nos premiers carnets arrivent bientôt.</p>
              <p className="text-sm text-[#737373] mt-3">En attendant, composons ensemble le vôtre.</p>
              <a href="/#devis" className="mt-7 inline-block bg-[#1A1B18] text-[#FAF9F6] px-8 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-[#96836B] transition-colors">
                Créer mon voyage sur-mesure
              </a>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="carnets-grid">
              {items.map((c) => (
                <Link key={c.public_slug} to={`/carnets/${c.public_slug}`} data-testid={`carnet-card-${c.public_slug}`} className="group block">
                  <div className="aspect-[4/3] overflow-hidden bg-[#E4DCCF]">
                    {c.cover_image ? (
                      <img src={c.cover_image} alt={c.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-[#E4DCCF] to-[#d3c7b2] text-[#7A4B3B]">
                        <BookOpen className="h-8 w-8" strokeWidth={1.2} />
                        <span className="text-[10px] uppercase tracking-[0.22em]">Carnet de voyage</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-5">
                    <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.18em] text-[#96836B]">
                      {c.destination && <span>{c.destination}</span>}
                      {c.trip_type && <span className="before:content-['·'] before:mr-3">{c.trip_type}</span>}
                      {c.duration_label && <span className="before:content-['·'] before:mr-3">{c.duration_label}</span>}
                    </div>
                    <h2 className="font-serif-display text-2xl md:text-3xl mt-2 leading-tight">{c.title}</h2>
                    {(c.public_intro || c.subtitle) && (
                      <p className="text-sm text-[#737373] mt-2 leading-relaxed line-clamp-2">{c.public_intro || c.subtitle}</p>
                    )}
                    <span className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#1A1B18] group-hover:text-[#96836B] transition-colors">
                      Consulter le carnet <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
